import RestartAlt from "@mui/icons-material/RestartAlt";
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { useRequeueActivities } from "hooks/activities/requeue";
import { compact } from "lodash";
import { useCallback, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useBulkActionContext } from "./BulkActionsMenu";

type RequeueForm = {
  comment: string | null;
};

export function RequeueMenuItem() {
  const { selectedRows, setDialogState, onClose } = useBulkActionContext();
  const hasSelection = selectedRows.some((row) => Boolean(row.activityId));

  const handleOpenDialog = useCallback(() => {
    onClose();
    setDialogState("requeue");
  }, [onClose, setDialogState]);

  return (
    <MenuItem onClick={handleOpenDialog} disabled={!hasSelection}>
      <ListItemIcon>
        <RestartAlt />
      </ListItemIcon>
      <ListItemText>Requeue</ListItemText>
    </MenuItem>
  );
}

export function RequeueDialog() {
  const alerts = useAlerts();
  const { dialogState, onClose, selectedRows } =
    useBulkActionContext();
  const [requeueMutation, { loading }] = useRequeueActivities();
  const form = useForm<RequeueForm>({
    defaultValues: { comment: "" },
    mode: "onChange",
  });
  const { handleSubmit, reset } = form;

  const activityIds = useMemo(
    () => compact(selectedRows.map((row) => row.activityId)),
    [selectedRows]
  );
  const isOpen = dialogState === "requeue";

  const handleClose = useCallback(() => {
    reset({ comment: "" });
    onClose();
  }, [onClose, reset]);

  const onSubmit = handleSubmit(async ({ comment }) => {
    if (!activityIds.length) {
      handleClose();
      return;
    }


    const trimmedComment = (comment ?? "").trim();
    try {
      const res = await requeueMutation({
        variables: {
          activityIds,
          comment: trimmedComment ? trimmedComment : undefined,
        },
      });

      const updatedCount = res.data?.requeueActivities?.updatedCount ?? 0;

      if (activityIds.length === updatedCount) {
        alerts.success("Successfully requeued activities!");
      } else if (updatedCount === 0) {
        alerts.error("No activities were requeued.");
      } else {
        alerts.info(
          "Some activities were requeued, but some failed. Refresh to investigate"
        );
      }
      handleClose();
    } catch (error) {
      alerts.error("Failed to requeue activities.", error);
    }
  });

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      title="Requeue Activities"
      primaryActionButtonProps={{
        onClick: onSubmit,
        loading,
        loadingText: "Requeuing...",
        disabled: loading || activityIds.length === 0,
        saveText: "Requeue",
      }}
    >
      <FormProvider {...form}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: "400px",
          }}
        >
          <Typography>
            You are about to requeue {activityIds.length}{" "}
            {activityIds.length === 1 ? "activity" : "activities"}.
          </Typography>
          <FormTextField<RequeueForm>
            name="comment"
            label="Reason (optional)"
            maxLength={400}
            textFieldProps={{
              multiline: true,
              minRows: 6,
              placeholder:
                "Add context for why these activities are being requeued",
            }}
          />
        </Box>
      </FormProvider>
    </Dialog>
  );
}
